import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { createFileRoute, redirect } from "@tanstack/react-router"
import type { ReservationNoShowFeeConfig } from "ipctg-inflow-types/InFlowDB"
import { CheckoutFailReason } from "ipctg-inflow-types/InFlowEnums"
import { FormEvent, useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Checkbox from "@/globalComponents/atoms/Checkbox"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import StripeElementsWrapper from "@/globalComponents/molecules/StripeElementsWrapper"
import {
  ExpectedError,
  ReservePaymentIntentError,
} from "@/utilities/expectedErrors"
import {
  reserveCheckoutQueryOptions,
  reservePaymentIntentQueryOptions,
  reserveSetupIntentQueryOptions,
} from "@/utilities/hooks/queries/reserve"
import { useStripePaymentElement } from "@/utilities/hooks/useStripePaymentElement"
import { log } from "@/utilities/logging"
import { prettyCurrencyNumber } from "@/utilities/normalizers"

import styles from "./checkout.module.css"

// No-show reservations run Stripe in "setup" mode (save a card, capture the
// fee only on a no-show); everything else runs in "payment" mode (charge now).
type ReserveCheckoutContext =
  | {
      mode: "setup"
      clientSecret: string
      customerSessionClientSecret: string | null
      amountDue: number
      noShowFeeConfig: ReservationNoShowFeeConfig
    }
  | {
      mode: "payment"
      clientSecret: string
      customerSessionClientSecret: string | null
      amountDue: number
    }

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/checkout"
)({
  staticData: {
    pageName: "Checkout",
  },
  beforeLoad: async ({ context, params }) => {
    const checkout = await context.queryClient.fetchQuery(
      reserveCheckoutQueryOptions()
    )
    const noShowFeeConfig =
      checkout.transactionDetails.reservationNoShowFeeConfig

    // No-show reservations save a card on file (SetupIntent) instead of
    // charging now (PaymentIntent). The fee is only captured on a no-show.
    if (noShowFeeConfig) {
      const setupIntent = await context.queryClient.fetchQuery(
        reserveSetupIntentQueryOptions()
      )

      if (!setupIntent.clientSecret) {
        throw redirect({
          to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/confirmation",
          params,
          search: { noShow: true },
        })
      }

      return {
        checkout: {
          mode: "setup",
          clientSecret: setupIntent.clientSecret,
          customerSessionClientSecret: setupIntent.customerSessionClientSecret,
          amountDue: checkout.transactionDetails.amountDue,
          noShowFeeConfig,
        } satisfies ReserveCheckoutContext,
      }
    }

    let paymentIntent
    try {
      paymentIntent = await context.queryClient.fetchQuery(
        reservePaymentIntentQueryOptions()
      )
    } catch (e) {
      if (e instanceof ReservePaymentIntentError) {
        if (e.failReason === CheckoutFailReason.PaymentAlreadyCompleted) {
          throw redirect({
            to: "/qr/$qrLookupID/reserve/$epoch/payment-complete",
            params,
          })
        }
      }
      throw e
    }

    if (!paymentIntent.requiresPayment) {
      throw redirect({
        to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/confirmation",
        params,
        search: { noShow: false },
      })
    }

    return {
      checkout: {
        mode: "payment",
        clientSecret: paymentIntent.clientSecret!,
        customerSessionClientSecret: paymentIntent.customerSessionClientSecret,
        amountDue: paymentIntent.amountDue,
      } satisfies ReserveCheckoutContext,
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { checkout } = Route.useRouteContext()
  const { t } = useTranslation(["reserve"])
  return (
    <StripeElementsWrapper
      clientSecret={checkout.clientSecret}
      customerSessionClientSecret={
        checkout.customerSessionClientSecret || undefined
      }
      flow="reserve"
    >
      <Card>
        <Heading level={1} as={2}>
          {t("cart.paymentDetails", { ns: "reserve" })}
        </Heading>
        {checkout.mode === "setup" && (
          <Text level={2} style={{ marginTop: "var(--space-2)" }}>
            {t("cart.paymentDetailsNoShowDescription", { ns: "reserve" })}
          </Text>
        )}
        <CheckoutForm
          mode={checkout.mode}
          amountDue={checkout.amountDue}
          noShowFeeConfig={
            checkout.mode === "setup" ? checkout.noShowFeeConfig : null
          }
          goNext={() =>
            navigate({
              to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/confirmation",
              search: { noShow: checkout.mode === "setup" },
            })
          }
        />
      </Card>
    </StripeElementsWrapper>
  )
}

interface CheckoutFormProps {
  mode: "setup" | "payment"
  amountDue: number
  noShowFeeConfig: ReservationNoShowFeeConfig | null
  goNext: () => void
}

function CheckoutForm(props: CheckoutFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation(["common", "errors", "reserve"])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error>()
  const [agreedToNoShowFee, setAgreedToNoShowFee] = useState(false)
  const [noShowFeeError, setNoShowFeeError] = useState(false)

  const { paymentElementProps, stripeError } = useStripePaymentElement({
    flow: "reserve",
  })

  if (stripeError) throw stripeError

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || isLoading}>
        <PaymentElement
          {...paymentElementProps}
          options={{ terms: { card: "never" } }}
        />
        <Separator />
        <div className={styles.dueToday}>
          <Heading level={4} as={3}>
            {t("cart.dueToday")}
          </Heading>
          <Text level={2} className="semi-bold">
            {prettyCurrencyNumber(Math.max(0, props.amountDue))}
          </Text>
        </div>
        {props.mode === "setup" && props.noShowFeeConfig && (
          // The box and its error are one fieldset grid item, so the fieldset's
          // gap doesn't push the error away from the checkbox it belongs to.
          <div className={styles.noShowFeeSection}>
            <div
              className={styles.noShowFeeAgreement}
              data-invalid={noShowFeeError || undefined}
            >
              <Checkbox
                className={styles.checkboxField}
                value={agreedToNoShowFee}
                isInvalid={noShowFeeError}
                onChange={(isSelected) => {
                  setAgreedToNoShowFee(isSelected)
                  if (isSelected) setNoShowFeeError(false)
                }}
              >
                <div style={{ marginTop: "2px" }}>
                  <Trans
                    i18nKey="cart.noShowFeeAgreement"
                    ns="reserve"
                    values={{
                      amount: prettyCurrencyNumber(
                        props.noShowFeeConfig.amount,
                      ),
                    }}
                    components={{ 1: <strong /> }}
                  />
                </div>
              </Checkbox>
            </div>
            {noShowFeeError && (
              <FieldError>
                {t("cart.noShowFeeAgreementRequired", { ns: "reserve" })}
              </FieldError>
            )}
          </div>
        )}
        {error && (
          <FieldError>
            {error instanceof ExpectedError
              ? t(error.translationKey, { ns: "errors" })
              : error.message}
          </FieldError>
        )}
        <Button type="submit" buttonType="primary" isPending={isLoading}>
          {t("cart.confirmReservation", { ns: "reserve" })}
        </Button>
      </fieldset>
    </form>
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (props.noShowFeeConfig && !agreedToNoShowFee) {
      setNoShowFeeError(true)
      return
    }

    setIsLoading(true)

    if (!stripe || !elements) {
      setIsLoading(false)
      return
    }

    const paymentElement = elements.getElement(PaymentElement)
    if (!paymentElement) {
      setError(new Error(t("common.paymentDetailsNotFound", { ns: "errors" })))
      setIsLoading(false)
      return
    }

    try {
      const result =
        props.mode === "setup"
          ? await stripe.confirmSetup({
              elements,
              redirect: "if_required",
            })
          : await stripe.confirmPayment({
              elements,
              redirect: "if_required",
            })

      if (result.error) {
        log({
          level: "error",
          flow: "reserve",
          scope:
            props.mode === "setup"
              ? "payment.stripeConfirmSetup"
              : "payment.stripeConfirmPayment",
          message: `Stripe ${
            props.mode === "setup" ? "confirmSetup" : "confirmPayment"
          } returned an error`,
          meta: {
            code: result.error.code,
            type: result.error.type,
            declineCode: result.error.decline_code,
          },
          sentry: "exception",
          exception: result.error,
        })
        setError(new Error(result.error.message))
        return
      }

      await new Promise((resolve) => setTimeout(resolve, 1000))
      props.goNext()
    } catch (e: unknown) {
      log({
        level: "error",
        flow: "reserve",
        scope:
          props.mode === "setup"
            ? "payment.confirmSetup"
            : "payment.confirmPayment",
        message: "Reserve payment confirmation failed",
        meta: { error: e },
        sentry: e instanceof ExpectedError ? "breadcrumb" : "exception",
        exception: e instanceof Error ? e : new Error(String(e)),
      })
      if (e instanceof Error) {
        setError(e)
      } else {
        setError(new Error(t("common.somethingWentWrong", { ns: "errors" })))
      }
    } finally {
      setIsLoading(false)
    }
  }
}
